import React from 'react';
import PublicLayout from '../../components/layouts/PublicLayout';
import { useSEO, generateBreadcrumbSchema, generateWebPageSchema } from '../../hooks/useSEO';
import { usePublicTranslation } from '../../hooks/usePublicTranslation';
import { FileText, Palette, Type } from 'lucide-react';

const PressPage: React.FC = () => {
    const { currentLanguage } = usePublicTranslation();

    // Translations for press page
    const press = {
        hero: {
            title: currentLanguage === 'en' ? 'Press & Media' : currentLanguage === 'es' ? 'Prensa y Medios' : 'Imprensa e Mídia',
            subtitle: currentLanguage === 'en'
                ? 'Download our official brand materials for press and media use'
                : currentLanguage === 'es'
                    ? 'Descargue nuestros materiales oficiales de marca para uso de prensa y medios'
                    : 'Baixe nossos materiais oficiais da marca para uso de imprensa e mídia'
        },
        mediaKit: {
            title: currentLanguage === 'en' ? 'Media Kit' : currentLanguage === 'es' ? 'Kit de Medios' : 'Media Kit',
            subtitle: currentLanguage === 'en'
                ? 'Access our complete brand guide with logos, colors, typography and usage guidelines'
                : currentLanguage === 'es'
                    ? 'Acceda a nuestra guía de marca completa con logotipos, colores, tipografía y directrices de uso'
                    : 'Acesse nosso guia completo da marca com logotipos, cores, tipografia e diretrizes de uso',
            downloadPdf: currentLanguage === 'en' ? 'View PDF' : currentLanguage === 'es' ? 'Ver PDF' : 'Ver PDF',
            logos: currentLanguage === 'en' ? 'Official Logos' : currentLanguage === 'es' ? 'Logotipos Oficiales' : 'Logotipos Oficiais',
            colors: currentLanguage === 'en' ? 'Color Palette' : currentLanguage === 'es' ? 'Paleta de Colores' : 'Paleta de Cores',
            typography: currentLanguage === 'en' ? 'Typography' : currentLanguage === 'es' ? 'Tipografía' : 'Tipografia',
            guidelines: currentLanguage === 'en' ? 'Usage Guidelines' : currentLanguage === 'es' ? 'Directrices de Uso' : 'Diretrizes de Uso'
        }
    };

    // SEO Hook
    useSEO({
        title: press.hero.title,
        description: press.hero.subtitle,
        path: "/press",
        language: currentLanguage as 'pt-br' | 'en' | 'es',
        structuredData: [
            generateBreadcrumbSchema([
                { name: 'Home', url: '/' },
                { name: 'Imprensa', url: '/press' }
            ]),
            generateWebPageSchema({
                name: press.hero.title,
                description: press.hero.subtitle,
                url: "/press",
                datePublished: "2025-01-20",
                dateModified: "2025-01-20"
            })
        ]
    });

    return (
        <PublicLayout>
            <main className="flex-grow bg-gray-50">
                {/* Hero Section */}
                <section className="bg-gradient-to-br from-dark to-primary-dark text-white py-16 md:py-24">
                    <div className="container mx-auto px-4 text-center">
                        <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-white to-secondary bg-clip-text text-transparent">
                            {press.hero.title}
                        </h1>
                        <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto leading-relaxed">
                            {press.hero.subtitle}
                        </p>
                    </div>
                </section>

                {/* Media Kit Section */}
                <section className="py-16 md:py-24 bg-white">
                    <div className="container mx-auto px-4">
                        <div className="max-w-4xl mx-auto">
                            <div className="bg-gradient-to-br from-dark to-primary-dark rounded-2xl overflow-hidden shadow-2xl">
                                <div className="p-8 md:p-12">
                                    <div className="flex flex-col md:flex-row items-center gap-8">
                                        {/* Left - Info */}
                                        <div className="flex-1 text-center md:text-left">
                                            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                                                {press.mediaKit.title}
                                            </h2>
                                            <p className="text-white/80 text-lg mb-8">
                                                {press.mediaKit.subtitle}
                                            </p>

                                            {/* Features */}
                                            <div className="grid grid-cols-2 gap-4 mb-8">
                                                <div className="flex items-center gap-3 text-white/90">
                                                    <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                                                        <FileText size={20} className="text-secondary" />
                                                    </div>
                                                    <span className="text-sm">{press.mediaKit.logos}</span>
                                                </div>
                                                <div className="flex items-center gap-3 text-white/90">
                                                    <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                                                        <Palette size={20} className="text-secondary" />
                                                    </div>
                                                    <span className="text-sm">{press.mediaKit.colors}</span>
                                                </div>
                                                <div className="flex items-center gap-3 text-white/90">
                                                    <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                                                        <Type size={20} className="text-secondary" />
                                                    </div>
                                                    <span className="text-sm">{press.mediaKit.typography}</span>
                                                </div>
                                                <div className="flex items-center gap-3 text-white/90">
                                                    <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                                                        <FileText size={20} className="text-secondary" />
                                                    </div>
                                                    <span className="text-sm">{press.mediaKit.guidelines}</span>
                                                </div>
                                            </div>

                                            {/* View PDF Button */}
                                            <a
                                                href="/NexuCapital-Media-Kit.pdf"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-3 px-8 py-4 bg-secondary hover:bg-secondary-dark text-white font-bold rounded-xl transition-all hover:-translate-y-1 shadow-lg hover:shadow-xl"
                                            >
                                                <FileText size={22} />
                                                {press.mediaKit.downloadPdf}
                                            </a>
                                        </div>

                                        {/* Right - Visual */}
                                        <div className="hidden md:block">
                                            <div className="relative">
                                                <div className="w-48 h-64 bg-white/5 rounded-lg border border-white/10 flex items-center justify-center backdrop-blur-sm">
                                                    <div className="text-center">
                                                        <div className="w-20 h-20 mx-auto mb-4 bg-white/10 rounded-full flex items-center justify-center">
                                                            <FileText size={36} className="text-secondary" />
                                                        </div>
                                                        <p className="text-white/60 text-sm">PDF</p>
                                                        <p className="text-white font-semibold">Media Kit</p>
                                                    </div>
                                                </div>
                                                {/* Decorative elements */}
                                                <div className="absolute -top-4 -right-4 w-24 h-24 bg-secondary/20 rounded-full blur-2xl"></div>
                                                <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-primary/30 rounded-full blur-2xl"></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
        </PublicLayout>
    );
};

export default PressPage;
